import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const serviceDetails = {
  'revicion-sistematica': {
    title: 'Revisión Sistemática',
    subtitle: 'Análisis exhaustivo y riguroso de toda la literatura disponible sobre tu tema de investigación, siguiendo protocolos PRISMA y metodologías reconocidas internacionalmente.',
    price: 550,
    time: '2-4 semanas',
    includes: [
      'Búsqueda exhaustiva en bases de datos (PubMed, Scopus, Web of Science, Google Scholar)',
      'Identificación y selección de estudios relevantes según criterios PRISMA',
      'Análisis crítico de la calidad metodológica de los estudios',
      'Matriz de extracción de datos completa y organizada',
      'Síntesis narrativa de hallazgos clave',
      'Gráfica de flujo PRISMA (Preferred Reporting Items)',
      'Reporte detallado con recomendaciones',
      'Garantía antiplagio con reporte Turnitin'
    ],
    process: [
      { step: 1, title: 'Definición de la pregunta de investigación', desc: 'Clarificamos tu tema, objetivos y criterios de inclusión/exclusión' },
      { step: 2, title: 'Búsqueda sistemática de literatura', desc: 'Rastreo exhaustivo en múltiples bases de datos con términos MeSH y booleanos' },
      { step: 3, title: 'Selección de estudios', desc: 'Filtrado de artículos relevantes según criterios predefinidos' },
      { step: 4, title: 'Extracción de datos', desc: 'Recopilación sistemática de información en matriz estructurada' },
      { step: 5, title: 'Análisis de calidad', desc: 'Evaluación del riesgo de sesgo usando herramientas reconocidas' },
      { step: 6, title: 'Síntesis e informe final', desc: 'Reporte completo con conclusiones y recomendaciones basadas en evidencia' }
    ]
  },
  'proyecto': {
    title: 'Proyecto',
    subtitle: 'Desarrollo completo de proyectos académicos y profesionales. Desde la formulación hasta la implementación con enfoque innovador.',
    price: 700,
    time: '2-4 semanas',
    includes: [
      'Formulación clara de objetivos y justificación',
      'Diagnóstico profundo de la realidad problemática',
      'Diseño de soluciones innovadoras y viables',
      'Planificación detallada de actividades',
      'Presupuesto realista y fundamentado',
      'Cronograma ejecutable con hitos claros',
      'Evaluación y monitoreo de resultados',
      'Documento profesional con anexos',
      'Presentación visual en diapositivas'
    ],
    process: [
      { step: 1, title: 'Identificación del problema', desc: 'Análisis profundo de la situación inicial' },
      { step: 2, title: 'Formulación de objetivos', desc: 'Definición clara de metas y resultados esperados' },
      { step: 3, title: 'Diseño de intervención', desc: 'Estrategia y actividades para lograr objetivos' },
      { step: 4, title: 'Planificación operativa', desc: 'Cronograma, responsables, recursos y presupuesto' },
      { step: 5, title: 'Redacción del documento', desc: 'Proyecto completo en formato profesional' },
      { step: 6, title: 'Presentación', desc: 'Diapositivas y preparación para defensa' }
    ]
  },
  'tesis-de-pregrado': {
    title: 'Tesis de Pregrado',
    subtitle: 'Desarrollo completo de tu tesis desde la propuesta hasta la defensa. Asesoría integral con garantía de éxito.',
    price: 1500,
    time: '3-6 semanas',
    includes: [
      'Diseño completo del estudio (planteamiento, objetivos, hipótesis)',
      'Marco teórico exhaustivo con referenciación APA',
      'Metodología rigurosa con muestreo y técnicas definidas',
      'Recolección y análisis de datos profesional',
      'Interpretación de resultados con discusión académica',
      'Redacción clara y coherente en formato universitario',
      'Diseño de diapositivas para presentación',
      'Preparación y simulación de defensa',
      'Garantía antiplagio Turnitin incluida'
    ],
    process: [
      { step: 1, title: 'Planteamiento del problema', desc: 'Definición clara y fundamentada del tema' },
      { step: 2, title: 'Marco teórico', desc: 'Recopilación y síntesis de teorías relevantes' },
      { step: 3, title: 'Metodología', desc: 'Diseño del enfoque, muestra y técnicas' },
      { step: 4, title: 'Recolección de datos', desc: 'Aplicación de instrumentos y recepción de datos' },
      { step: 5, title: 'Análisis y resultados', desc: 'Procesamiento estadístico o cualitativo' },
      { step: 6, title: 'Redacción y defensa', desc: 'Documento final + preparación de presentación' }
    ]
  },
  'datos-estadisticos': {
    title: 'Análisis de Datos Estadísticos',
    subtitle: 'Análisis profesional de tus datos utilizando herramientas avanzadas. SPSS, R, Python y más.',
    price: 300,
    time: '1-2 semanas',
    includes: [
      'Importación y limpieza de datos',
      'Análisis estadístico descriptivo completo',
      'Pruebas de hipótesis apropiadas',
      'Análisis de varianza (ANOVA), regresión, correlación',
      'Modelamiento estadístico avanzado',
      'Generación de gráficos profesionales',
      'Interpretación de resultados en contexto',
      'Reporte técnico detallado',
      'Asesoría sobre métodos estadísticos'
    ],
    process: [
      { step: 1, title: 'Recepción de datos', desc: 'Entrega de base de datos en cualquier formato' },
      { step: 2, title: 'Limpieza y preparación', desc: 'Validación y preparación de datos' },
      { step: 3, title: 'Análisis exploratorio', desc: 'Estadísticas descriptivas y visualización' },
      { step: 4, title: 'Análisis inferencial', desc: 'Pruebas estadísticas según objetivos' },
      { step: 5, title: 'Modelamiento', desc: 'Construcción de modelos predictivos si aplica' },
      { step: 6, title: 'Reporte final', desc: 'Interpretación de resultados y conclusiones' }
    ]
  },
  'tesis-de-maestria': {
    title: 'Tesis de Maestría',
    subtitle: 'Tesis de postgrado con rigor académico de nivel internacional. Investigación original y con impacto.',
    price: 2600,
    time: '6-10 semanas',
    includes: [
      'Revisión sistemática de literatura especializada',
      'Planteamiento innovador del problema de investigación',
      'Marco teórico profundo y crítico',
      'Metodología avanzada con validación de instrumentos',
      'Análisis estadístico o cualitativo riguroso',
      'Discusión con contribuciones al conocimiento',
      'Redacción académica de nivel publicable',
      'Correcciones ilimitadas hasta aprobación',
      'Preparación completa para defensa pública'
    ],
    process: [
      { step: 1, title: 'Evaluación inicial', desc: 'Análisis profundo del tema y alcance' },
      { step: 2, title: 'Revisión sistemática', desc: 'Búsqueda exhaustiva de literatura' },
      { step: 3, title: 'Diseño metodológico', desc: 'Enfoque riguroso y validación de instrumentos' },
      { step: 4, title: 'Ejecución', desc: 'Recolección y análisis de datos' },
      { step: 5, title: 'Redacción académica', desc: 'Discusión y aportaciones originales' },
      { step: 6, title: 'Defensa', desc: 'Preparación y simulación para presentación' }
    ]
  },
  'trabajo-de-suficiencia-profesional': {
    title: 'Trabajo de Suficiencia Profesional',
    subtitle: 'Demostración de competencias profesionales con base académica sólida y aplicada.',
    price: 1300,
    time: '4-8 semanas',
    includes: [
      'Análisis de caso o experiencia profesional',
      'Marco teórico aplicado a la práctica',
      'Planteamiento del problema profesional',
      'Soluciones y recomendaciones implementadas',
      'Evaluación de resultados y logros',
      'Documentación profesional de calidad',
      'Anexos con evidencia de implementación',
      'Diseño de diapositivas profesional',
      'Asesoría para exposición oral'
    ],
    process: [
      { step: 1, title: 'Selección de caso', desc: 'Identificación del caso profesional relevante' },
      { step: 2, title: 'Marco teórico', desc: 'Fundamentación teórica de la experiencia' },
      { step: 3, title: 'Análisis', desc: 'Evaluación profunda del contexto y soluciones' },
      { step: 4, title: 'Resultados', desc: 'Documentación de logros y aprendizajes' },
      { step: 5, title: 'Redacción', desc: 'Documento profesional y coherente' },
      { step: 6, title: 'Presentación', desc: 'Preparación para exposición oral' }
    ]
  },
  'planes-de-negocio': {
    title: 'Planes de Negocio',
    subtitle: 'Planes de negocio profesionales que abren puertas a inversión. Diseñados para convencer a inversionistas.',
    price: 2800,
    time: '4-8 semanas',
    includes: [
      'Resumen ejecutivo impactante',
      'Análisis profundo del mercado y competencia',
      'Descripción detallada de la propuesta de valor',
      'Modelo de negocio y estructura organizacional',
      'Estrategia de marketing y ventas',
      'Proyecciones financieras detalladas',
      'Plan de operaciones e implementación',
      'Análisis de riesgos y mitigación',
      'Presentación ejecutiva profesional'
    ],
    process: [
      { step: 1, title: 'Validación de idea', desc: 'Análisis inicial del concepto de negocio' },
      { step: 2, title: 'Investigación de mercado', desc: 'Estudios de competencia y oportunidades' },
      { step: 3, title: 'Modelo de negocio', desc: 'Definición de estructura y operaciones' },
      { step: 4, title: 'Estrategia comercial', desc: 'Plan de marketing y ventas detallado' },
      { step: 5, title: 'Proyecciones financieras', desc: 'Simulaciones y análisis de viabilidad' },
      { step: 6, title: 'Presentación final', desc: 'Documento completo y pitch ejecutivo' }
    ]
  },
  'analisis-expedientes': {
    title: 'Análisis de Expedientes',
    subtitle: 'Análisis jurídico exhaustivo de expedientes legales. Asesoría especializada para casos complejos.',
    price: 900,
    time: '2-3 semanas',
    includes: [
      'Revisión completa del expediente y antecedentes',
      'Análisis de puntos controvertidos',
      'Evaluación de pruebas y evidencia',
      'Identificación de vulnerabilidades procesales',
      'Análisis de jurisprudencia relevante',
      'Estrategia jurídica recomendada',
      'Conclusiones y recomendaciones',
      'Reporte detallado con hallazgos clave',
      'Asesoría para próximos pasos legales'
    ],
    process: [
      { step: 1, title: 'Recepción de expediente', desc: 'Entrega completa de documentación legal' },
      { step: 2, title: 'Lectura integral', desc: 'Revisión profunda de todos los documentos' },
      { step: 3, title: 'Análisis jurídico', desc: 'Evaluación de argumentos y jurisprudencia' },
      { step: 4, title: 'Identificación de puntos clave', desc: 'Determinación de fortalezas y debilidades' },
      { step: 5, title: 'Estrategia legal', desc: 'Recomendaciones basadas en análisis' },
      { step: 6, title: 'Informe final', desc: 'Reporte con conclusiones y próximos pasos' }
    ]
  },
  'trabajos-ciclo': {
    title: 'Trabajos de Ciclo',
    subtitle: 'Trabajos de fin de ciclo profesionales con metodología y rigor académico garantizado.',
    price: 'Según estructura',
    time: '2-4 semanas',
    includes: [
      'Asesoría en selección de tema',
      'Diseño de investigación apropiado',
      'Marco teórico adaptado al nivel',
      'Metodología clara y documentada',
      'Análisis de datos o información',
      'Redacción clara y bien estructurada',
      'Formato académico profesional',
      'Revisión y correcciones ilimitadas',
      'Garantía antiplagio Turnitin'
    ],
    process: [
      { step: 1, title: 'Consultoría inicial', desc: 'Definición del tema y alcance del trabajo' },
      { step: 2, title: 'Estructuración', desc: 'Diseño del esquema y objetivos' },
      { step: 3, title: 'Investigación', desc: 'Recopilación de información y fuentes' },
      { step: 4, title: 'Análisis', desc: 'Interpretación de datos según metodología' },
      { step: 5, title: 'Redacción', desc: 'Elaboración del documento completo' },
      { step: 6, title: 'Presentación', desc: 'Formato final y preparación de exposición' }
    ]
  }
};

function renderServiceIcon(key, className = '') {
  const k = (key || '').toString().toLowerCase();
  // Graduation cap
  if (k.includes('tesis')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#FFD400" />
        <path d="M2 7v6c0 2.761 4.478 5 10 5s10-2.239 10-5V7" stroke="#222" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // Bar chart for data
  if (k.includes('datos') || k.includes('estadistic')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="12" width="3.5" height="8" rx="0.5" fill="#FFD400" />
        <rect x="9" y="8" width="3.5" height="12" rx="0.5" fill="#FFB733" />
        <rect x="15" y="4" width="3.5" height="16" rx="0.5" fill="#FF9F1C" />
      </svg>
    );
  }
  // Magnifier over document for systematic review / analysis
  if (k.includes('revision') || k.includes('revis') || k.includes('analisis')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="14" height="14" rx="1.5" fill="#FFD400" />
        <path d="M21 21l-4.35-4.35" stroke="#222" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="16.5" cy="16.5" r="2" stroke="#222" strokeWidth="1.2" />
      </svg>
    );
  }
  // Project / clipboard
  if (k.includes('proyecto') || k.includes('proyect')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="5" width="16" height="14" rx="1.5" fill="#FFD400" />
        <path d="M8 3h8v4H8z" stroke="#222" strokeWidth="0.9" fill="none" />
        <path d="M9 10h6M9 13h6" stroke="#222" strokeWidth="0.9" strokeLinecap="round" />
      </svg>
    );
  }
  // Briefcase / business
  if (k.includes('planes') || k.includes('negocio') || k.includes('plan')) {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="8" width="18" height="11" rx="1.5" fill="#FFD400" />
        <path d="M8 8V6a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#222" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // Default document icon
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" fill="#FFD400" />
      <path d="M14 2v6h6" stroke="#222" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ServiceDetailPage({ serviceName }) {
  const service = serviceDetails[serviceName];
  if (!service) return <div className="pt-24">Servicio no encontrado</div>;

  const whatsappUrl = `https://wa.me/+51983318651?text=Hola, me interesa el servicio de ${service.title}`;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="pt-24 bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand/5 to-transparent border-b border-brand/20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-brand/10 to-transparent rounded-full blur-3xl -mr-48 -mt-48"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36 relative z-10">
          {/* Large animated service icon (positioned relative to content wrapper, vertically centered) */}
          <div className="absolute right-4 sm:right-8 top-1/2 transform -translate-y-1/2 block z-30 pointer-events-none">
            <motion.div
              initial={{ y: 0, rotate: -3 }}
              animate={{ y: [-6, 6, -6], rotate: [-2, 6, -2] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              whileHover={{ scale: 1.06 }}
              className="w-36 h-36 sm:w-48 sm:h-48 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-200/80 p-4 sm:p-5 flex items-center justify-center shadow-2xl border border-yellow-300"
            >
              <div className="w-full h-full flex items-center justify-center rounded-full bg-white/70 shadow-inner">
                {renderServiceIcon(serviceName, 'w-20 h-20 sm:w-28 sm:h-28')}
              </div>
            </motion.div>
          </div>
          <motion.div className="max-w-3xl md:pr-56 lg:pr-72" initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-50 to-yellow-100 border border-brand/30 mb-6">
              <span className="w-2 h-2 rounded-full bg-brand animate-pulse"></span>
              <span className="text-sm font-bold text-brand-dark tracking-wide">{service.title.toUpperCase()}</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.1] tracking-tight">
              {service.title}
            </h1>
            <p className="mt-6 text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-2xl">
              {service.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="text-sm font-bold text-brand-dark bg-yellow-100/50 px-4 py-2 rounded-full">
                Precio: S/ {service.price.toLocaleString()}
              </div>
              <div className="text-sm font-bold text-gray-700 bg-gray-100 px-4 py-2 rounded-full">
                Tiempo: {service.time}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.div className="lg:col-span-2 space-y-12" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              {serviceName === 'trabajo-de-suficiencia-profesional' ? (
                <div className="space-y-10">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Resumen Ejecutivo</h2>
                    <p className="text-gray-700">Breve síntesis de los objetivos, metodología, principales hallazgos y recomendaciones del trabajo de suficiencia profesional.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Capítulo I: Introducción</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Contextualización de la problemática</li>
                      <li>Ubicación geográfica de la empresa</li>
                      <li>Misión y visión</li>
                      <li>Valores organizacionales</li>
                      <li>Organigrama</li>
                      <li>Clientes y Proveedores</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Capítulo II: Marco Teórico</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Antecedentes de estudios similares</li>
                      <li>Definición de variables</li>
                      <li>Fundamento legal</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Capítulo III: Descripción de la Experiencia</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Inicio del vínculo profesional</li>
                      <li>Funciones del autor como asistente legal</li>
                      <li>Participación del personal clave</li>
                      <li>Metodología del proyecto</li>
                      <li>Herramientas utilizadas</li>
                      <li>Consideraciones éticas</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Capítulo IV: Resultados</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Diagnóstico inicial</li>
                      <li>Resultados cuantitativos</li>
                      <li>Tablas</li>
                      <li>Modelo previo vs. implementado</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Capítulo V: Conclusiones y Recomendaciones</h3>
                    <p className="text-gray-700">Conclusiones derivadas del análisis y recomendaciones prácticas y estratégicas para la empresa.</p>
                    <h4 className="mt-4 font-semibold text-gray-900">Recomendaciones</h4>
                    <ul className="list-disc list-inside text-gray-700">
                      <li>Recomendación 1</li>
                      <li>Recomendación 2</li>
                      <li>Recomendación 3</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Referencias Bibliográficas</h3>
                    <p className="text-gray-700">Listado de fuentes y referencias utilizadas siguiendo el formato requerido por la institución.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Anexos</h3>
                    <p className="text-gray-700">Documentos, evidencias, tablas complementarias y cualquier material adicional relevante.</p>
                  </div>
                </div>
              ) : serviceName === 'proyecto' ? (
                <div className="space-y-10">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Introducción</h2>
                    <p className="text-gray-700">Contextualización del proyecto, antecedentes relevantes y alcance general del trabajo. Presenta la motivación y la importancia del estudio.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Problema General</h3>
                    <p className="text-gray-700">Descripción clara y concreta del problema central que aborda el proyecto, su impacto y la situación actual que motiva la intervención.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Objetivo General y Específicos</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li><strong>Objetivo General:</strong> Declaración que sintetiza la meta principal del proyecto.</li>
                      <li><strong>Objetivos Específicos:</strong> Lista de objetivos concretos y medibles que permiten alcanzar el objetivo general.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Hipótesis General y Específicos</h3>
                    <p className="text-gray-700">Planteamiento de la(s) hipótesis principales y las hipótesis específicas o supuestos que el proyecto intenta demostrar o poner a prueba.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Justificación: Teórica / Práctica / Metodológica</h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li><strong>Teórica:</strong> Aporte al conocimiento y marco conceptual.</li>
                      <li><strong>Práctica:</strong> Relevancia y beneficios esperados para la comunidad/organización.</li>
                      <li><strong>Metodológica:</strong> Aportes o innovaciones en métodos, técnicas o procedimientos.</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Metodología</h3>
                    <p className="text-gray-700">Descripción del enfoque (cuantitativo, cualitativo o mixto), diseño, población y muestra, técnicas de recolección de datos, instrumentos, procedimientos y métodos de análisis.</p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Matriz de Consistencia y Operacionalidad</h3>
                    <p className="text-gray-700">Matriz que relaciona problema, objetivos, variables, indicadores, fuentes de verificación y técnicas de análisis. Presentación de la operacionalización de variables y criterios de medición.</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* ¿Qué incluye? */}
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">¿Qué incluye?</h2>
                    <div className="space-y-4">
                      {service.includes.map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-brand to-yellow-500 flex items-center justify-center text-white font-bold mt-0.5">✓</div>
                          <p className="text-gray-700 leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Proceso */}
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Nuestro Proceso</h2>
                    <div className="space-y-6">
                      {service.process.map((item, i) => (
                        <div key={i} className="flex gap-6">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-brand to-yellow-500 text-white font-bold text-lg">{item.step}</div>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                            <p className="mt-1 text-gray-600">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Por qué elegirnos */}
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">¿Por qué elegirnos?</h2>
                    <ul className="space-y-3">
                      {[
                        'Equipo de expertos especializados en tu área',
                        'Garantía de calidad con revisiones ilimitadas',
                        'Cumplimiento de plazos garantizado',
                        'Soporte continuo durante todo el proceso',
                        'Formatos personalizados según tus necesidades',
                        'Garantía antiplagio con Turnitin'
                      ].map((item, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="text-brand-dark font-bold">✓</span>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </motion.div>

            {/* Sidebar - CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-2 border-brand/20 rounded-3xl p-8">
                <div className="text-center">
                  <div className="text-5xl font-black bg-gradient-to-r from-brand-dark to-yellow-600 bg-clip-text text-transparent mb-2">
                    S/ {service.price.toLocaleString()}
                  </div>
                  <p className="text-sm text-gray-600 font-semibold">Servicio completo</p>
                </div>

                <div className="mt-8 space-y-4">
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.263c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.485"/>
                    </svg>
                    Solicitar Ahora
                  </a>
                  <Link
                    to="/contacto"
                    className="w-full block text-center px-6 py-3 bg-white border-2 border-brand/20 text-brand-dark font-bold rounded-xl hover:border-brand/40 transition-all"
                  >
                    Enviar Consulta
                  </Link>
                </div>

                <div className="mt-8 pt-8 border-t-2 border-brand/10 space-y-4 text-sm">
                  <div className="flex gap-2">
                    <span className="text-lg">⏱️</span>
                    <div>
                      <p className="font-bold text-gray-900">Tiempo de entrega</p>
                      <p className="text-gray-600">{service.time}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-lg">✅</span>
                    <div>
                      <p className="font-bold text-gray-900">Revisiones ilimitadas</p>
                      <p className="text-gray-600">Hasta tu satisfacción total</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-lg">🛡️</span>
                    <div>
                      <p className="font-bold text-gray-900">Garantía antiplagio</p>
                      <p className="text-gray-600">Turnitin incluido</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-gold text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6">
            ¿Listo para comenzar?
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 mb-8">
            Contáctanos hoy y comienza tu camino hacia el éxito académico.
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 shadow-lg transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.263c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.485"/>
            </svg>
            Chatear por WhatsApp
          </a>
        </div>
      </section>
    </motion.div>
  );
}
